import { useEffect } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Lock } from "lucide-react";
import { useTranslations } from "@/lib/translations";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { buildBreadcrumbList } from "@/lib/breadcrumbs";

const Privacy = () => {
  const { isRTL, language } = useTranslations();
  const isAr = language === "ar";
  const prose = `prose prose-sm prose-neutral dark:prose-invert max-w-none ${isRTL ? "text-right" : ""}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  usePageMeta({
    title: "Privacy Policy | ESG Invest",
    description:
      "ESG Invest privacy policy. Learn how we collect, use, and protect personal data for users of our Middle East ESG ratings and investor intelligence platform.",
    path: "/privacy",
  });

  useJsonLd(
    "ld-privacy-breadcrumbs",
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Privacy", path: "/privacy" },
    ]),
  );

  return (
    <Layout>
      <section className="relative text-white py-20 overflow-hidden" style={{ backgroundImage: 'url(/subscriptions-hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <Lock className="h-4 w-4 text-accent" />
            {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {isAr ? "آخر تحديث: 3 أبريل 2025" : "Last updated April 03, 2025"}
          </p>
        </div>
      </section>

      <div className="bg-se-gray-50 dark:bg-background py-14" data-testid="privacy-page">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 space-y-6">

          {/* Intro card */}
          <Card className="p-6 md:p-8 border-0 shadow-sm">
            <div className={prose}>
              {isAr ? (
                <>
                  <p>يصف إشعار الخصوصية هذا الخاص بشركة Sustainability Excellence Management Consulting Limited ("نحن" أو "لنا" أو "خاصتنا") كيف ولماذا قد نصل إلى معلوماتك الشخصية ونجمعها ونخزنها ونستخدمها و/أو نشاركها ("المعالجة") عند استخدامك لخدماتنا ("الخدمات")، بما في ذلك عند:</p>
                  <ul>
                    <li>زيارة موقعنا الإلكتروني على <a href="https://platform.esginvest.com" className="text-accent hover:underline" dir="ltr">https://platform.esginvest.com</a>، أو أي موقع آخر من مواقعنا يرتبط بإشعار الخصوصية هذا.</li>
                    <li>استخدام منصة ESG. منصة ESG هي أداة SaaS مبتكرة قائمة على السحابة مصممة لتبسيط وتحسين عملية إدارة بيانات ESG وإعداد التقارير للشركات.</li>
                    <li>التواصل معنا بأساليب أخرى ذات صلة، بما فيها المبيعات والتسويق والفعاليات.</li>
                  </ul>
                  <p>أسئلة أو مخاوف؟ ستساعدك قراءة إشعار الخصوصية هذا على فهم حقوقك وخياراتك المتعلقة بالخصوصية. إذا كنت لا توافق على سياساتنا وممارساتنا، يُرجى عدم استخدام خدماتنا. وإن كانت لديك أي أسئلة أو مخاوف، يُرجى التواصل معنا على <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline" dir="ltr">digital@sustainabilityexcellence.com</a>.</p>
                </>
              ) : (
                <>
                  <p>This Privacy Notice for Sustainability Excellence Management Consulting Limited ("we", "us", or "our") describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you:</p>
                  <ul>
                    <li>Visit our website at <a href="https://platform.esginvest.com" className="text-accent hover:underline">https://platform.esginvest.com</a>, or any website of ours that links to this Privacy Notice.</li>
                    <li>Use ESG Platform. The ESG Platform is an innovative, cloud-based SaaS tool designed to streamline and optimize the ESG data management and reporting process for companies.</li>
                    <li>Engage with us in other related ways, including any sales, marketing, or events.</li>
                  </ul>
                  <p>Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline">digital@sustainabilityexcellence.com</a>.</p>
                </>
              )}
            </div>
          </Card>

          {/* Summary card */}
          <Card className="p-6 md:p-8 border-0 shadow-sm">
            <h2 className="text-lg font-bold text-foreground mb-4">
              {isAr ? "ملخص النقاط الرئيسية" : "Summary of Key Points"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <ul>
                  <li><strong>ما المعلومات الشخصية التي نعالجها؟</strong> عند زيارة خدماتنا أو استخدامها، قد نعالج معلومات شخصية وفقاً لطريقة تفاعلك معنا والخيارات التي تتخذها.</li>
                  <li><strong>هل نعالج أي معلومات شخصية حساسة؟</strong> لا نعالج معلومات شخصية حساسة.</li>
                  <li><strong>هل نجمع أي معلومات من أطراف ثالثة؟</strong> لا نجمع أي معلومات من أطراف ثالثة.</li>
                  <li><strong>كيف نعالج معلوماتك؟</strong> نعالج معلوماتك لتقديم خدماتنا وتحسينها وإدارتها، وللتواصل معك، ولأغراض الأمان ومنع الاحتيال، وللامتثال للقانون.</li>
                  <li><strong>في أي مواقف ومع أي أطراف نشارك المعلومات الشخصية؟</strong> قد نشارك المعلومات في مواقف محددة ومع أطراف ثالثة بعينها.</li>
                  <li><strong>كيف نحافظ على أمان معلوماتك؟</strong> لدينا عمليات وإجراءات تنظيمية وتقنية ملائمة لحماية معلوماتك الشخصية.</li>
                  <li><strong>ما هي حقوقك؟</strong> بحسب موقعك الجغرافي، قد يمنحك قانون الخصوصية المعمول به حقوقاً معينة تتعلق بمعلوماتك الشخصية.</li>
                  <li><strong>كيف تمارس حقوقك؟</strong> أسهل طريقة لممارسة حقوقك هي تقديم طلب وصول موضوع البيانات، أو التواصل معنا مباشرة.</li>
                </ul>
              ) : (
                <ul>
                  <li><strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the choices you make.</li>
                  <li><strong>Do we process any sensitive personal information?</strong> We do not process sensitive personal information.</li>
                  <li><strong>Do we collect any information from third parties?</strong> We do not collect any information from third parties.</li>
                  <li><strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</li>
                  <li><strong>In what situations and with which parties do we share personal information?</strong> We may share information in specific situations and with specific third parties.</li>
                  <li><strong>How do we keep your information safe?</strong> We have adequate organizational and technical processes and procedures in place to protect your personal information.</li>
                  <li><strong>What are your rights?</strong> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.</li>
                  <li><strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by submitting a data subject access request, or by contacting us.</li>
                </ul>
              )}
            </div>
          </Card>

          {/* Section 1 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-1">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">1</span>
              {isAr ? "ما المعلومات التي نجمعها؟" : "What Information Do We Collect?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <h3>المعلومات الشخصية التي تُفصح عنها لنا</h3>
                  <p><em>باختصار: نجمع المعلومات الشخصية التي تزودنا بها.</em></p>
                  <p>نجمع المعلومات الشخصية التي تقدمها لنا طوعاً عند تسجيلك في الخدمات أو عند تواصلك معنا بأي طريقة أخرى. وقد تشمل المعلومات الشخصية التي نجمعها ما يلي:</p>
                  <ul>
                    <li>الأسماء</li>
                    <li>عناوين البريد الإلكتروني</li>
                    <li>المسميات الوظيفية</li>
                    <li>بيانات الاتصال أو المصادقة</li>
                  </ul>
                  <p><strong>المعلومات الحساسة.</strong> لا نعالج معلومات حساسة.</p>
                  <p><strong>بيانات الدفع.</strong> قد نجمع البيانات اللازمة لمعالجة دفعتك إذا اخترت إجراء عمليات شراء. تتولى Stripe معالجة جميع بيانات الدفع وتخزينها. يمكنك الاطلاع على سياسة الخصوصية الخاصة بهم هنا: <a href="https://stripe.com/ae/privacy" className="text-accent hover:underline" dir="ltr">https://stripe.com/ae/privacy</a>.</p>
                  <h3>المعلومات التي تُجمع تلقائياً</h3>
                  <p><em>باختصار: تُجمع بعض المعلومات تلقائياً عند زيارتك لخدماتنا، كعنوان IP ومواصفات المتصفح والجهاز.</em></p>
                  <p>نجمع تلقائياً معلومات معينة عند زيارتك أو استخدامك أو تنقلك في الخدمات، كعنوان IP ومواصفات المتصفح والجهاز ونظام التشغيل وتفضيلات اللغة وغيرها من المعلومات التقنية.</p>
                  <p><strong>واجهات برمجة تطبيقات Google.</strong> يلتزم استخدامنا للمعلومات المتلقاة من واجهات برمجة تطبيقات Google بسياسة بيانات مستخدمي خدمات Google API، بما في ذلك متطلبات الاستخدام المحدود.</p>
                </>
              ) : (
                <>
                  <h3>Personal information you disclose to us</h3>
                  <p><em>In Short: We collect personal information that you provide to us.</em></p>
                  <p>We collect personal information that you voluntarily provide to us when you register on the Services or otherwise contact us. The personal information we collect may include:</p>
                  <ul>
                    <li>Names</li>
                    <li>Email addresses</li>
                    <li>Job titles</li>
                    <li>Contact or authentication data</li>
                  </ul>
                  <p><strong>Sensitive Information.</strong> We do not process sensitive information.</p>
                  <p><strong>Payment Data.</strong> We may collect data necessary to process your payment if you choose to make purchases. All payment data is handled and stored by Stripe. You may find their privacy notice here: <a href="https://stripe.com/ae/privacy" className="text-accent hover:underline">https://stripe.com/ae/privacy</a>.</p>
                  <h3>Information automatically collected</h3>
                  <p><em>In Short: Some information — such as your IP address and browser/device characteristics — is collected automatically when you visit our Services.</em></p>
                  <p>We automatically collect certain information when you visit, use, or navigate the Services, such as your IP address, browser and device characteristics, operating system, language preferences, and other technical information.</p>
                  <p><strong>Google APIs.</strong> Our use of information received from Google APIs will adhere to Google API Services User Data Policy, including the Limited Use requirements.</p>
                </>
              )}
            </div>
          </Card>

          {/* Section 2 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-2">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">2</span>
              {isAr ? "كيف نعالج معلوماتك؟" : "How Do We Process Your Information?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <p><em>باختصار: نعالج معلوماتك لتقديم خدماتنا وتحسينها وإدارتها، وللتواصل معك، ولأغراض الأمان ومنع الاحتيال، وللامتثال للقانون.</em></p>
                  <p>نعالج معلوماتك الشخصية لأسباب متعددة، بما في ذلك:</p>
                  <ul>
                    <li><strong>تسهيل إنشاء الحساب والمصادقة</strong> وإدارة حسابات المستخدمين.</li>
                    <li><strong>تقديم الخدمات وتيسير تسليمها</strong> للمستخدم.</li>
                    <li><strong>تنفيذ طلباتك وإدارتها.</strong> قد نعالج معلوماتك لتنفيذ وإدارة طلباتك ومدفوعاتك التي تتم عبر الخدمات.</li>
                  </ul>
                </>
              ) : (
                <>
                  <p><em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</em></p>
                  <p>We process your personal information for a variety of reasons, including:</p>
                  <ul>
                    <li><strong>To facilitate account creation and authentication</strong> and manage user accounts.</li>
                    <li><strong>To deliver and facilitate delivery of services</strong> to the user.</li>
                    <li><strong>To fulfil and manage your orders.</strong> We may process your information to fulfil and manage your orders, payments, returns, and exchanges made through the Services.</li>
                  </ul>
                </>
              )}
            </div>
          </Card>

          {/* Section 3 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-3">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">3</span>
              {isAr ? "متى ومع من نشارك معلوماتك الشخصية؟" : "When and With Whom Do We Share Your Personal Information?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <p><em>باختصار: قد نشارك المعلومات في مواقف محددة موضحة في هذا القسم و/أو مع الأطراف الثالثة التالية.</em></p>
                  <p>قد نحتاج إلى مشاركة معلوماتك الشخصية في الحالات التالية:</p>
                  <ul>
                    <li><strong>التحويلات التجارية.</strong> قد نشارك معلوماتك أو نحولها فيما يتعلق بأي اندماج أو بيع لأصول الشركة أو تمويل أو استحواذ على كل أو جزء من أعمالنا لصالح شركة أخرى.</li>
                  </ul>
                </>
              ) : (
                <>
                  <p><em>In Short: We may share information in specific situations described in this section and/or with the following third parties.</em></p>
                  <p>We may need to share your personal information in the following situations:</p>
                  <ul>
                    <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
                  </ul>
                </>
              )}
            </div>
          </Card>

          {/* Section 4 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-4">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">4</span>
              {isAr ? "هل نقدم منتجات قائمة على الذكاء الاصطناعي؟" : "Do We Offer Artificial Intelligence-Based Products?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <p><em>باختصار: نقدم منتجات وميزات وأدوات مدعومة بالذكاء الاصطناعي أو التعلم الآلي أو تقنيات مماثلة.</em></p>
                  <p>في إطار خدماتنا، نقدم منتجات وميزات وأدوات مدعومة بالذكاء الاصطناعي (يُشار إليها بـ"منتجات الذكاء الاصطناعي"). نقدم هذه المنتجات عبر مزودي خدمات خارجيين، من بينهم Google Cloud AI.</p>
                  <p><strong>منتجات الذكاء الاصطناعي لدينا</strong> مصممة للوظائف التالية:</p>
                  <ul>
                    <li>تحليل النصوص</li>
                    <li>روبوتات الذكاء الاصطناعي</li>
                    <li>معالجة اللغة الطبيعية</li>
                  </ul>
                  <p>تتم معالجة جميع المعلومات الشخصية باستخدام منتجات الذكاء الاصطناعي لدينا وفقاً لإشعار الخصوصية هذا واتفاقيتنا مع الأطراف الثالثة.</p>
                </>
              ) : (
                <>
                  <p><em>In Short: We offer products, features, or tools powered by artificial intelligence, machine learning, or similar technologies.</em></p>
                  <p>As part of our Services, we offer products and features powered by artificial intelligence ("AI Products"), delivered through third-party service providers including Google Cloud AI.</p>
                  <p><strong>Our AI Products</strong> are designed for the following functions:</p>
                  <ul>
                    <li>Text analysis</li>
                    <li>AI bots</li>
                    <li>Natural language processing</li>
                  </ul>
                  <p>All personal information processed using our AI Products is handled in accordance with this Privacy Notice and our agreements with third parties.</p>
                </>
              )}
            </div>
          </Card>

          {/* Section 5 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-5">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">5</span>
              {isAr ? "كم من الوقت نحتفظ بمعلوماتك؟" : "How Long Do We Keep Your Information?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <p><em>باختصار: نحتفظ بمعلوماتك طالما كان ذلك ضرورياً لتحقيق الأغراض الموضحة في إشعار الخصوصية هذا ما لم يقتضِ القانون خلاف ذلك.</em></p>
                  <p>لن نحتفظ بمعلوماتك الشخصية إلا بالقدر الضروري للأغراض المحددة في إشعار الخصوصية هذا. لن يقتضي أي غرض في هذا الإشعار الاحتفاظ بمعلوماتك لفترة تتجاوز اثني عشر (12) شهراً بعد إنهاء حساب المستخدم.</p>
                  <p>حين لا تعود لدينا حاجة تجارية مشروعة لمعالجة معلوماتك الشخصية، سنقوم إما بحذفها أو إخفاء هويتها، أو الاحتفاظ بها بشكل آمن ومعزول.</p>
                </>
              ) : (
                <>
                  <p><em>In Short: We keep your information for as long as necessary to fulfil the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>
                  <p>We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice. No purpose in this notice will require us to keep your personal information for longer than twelve (12) months past the termination of the user's account.</p>
                  <p>When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymise it, or securely store it and isolate it from any further processing.</p>
                </>
              )}
            </div>
          </Card>

          {/* Section 6 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-6">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">6</span>
              {isAr ? "كيف نحافظ على أمان معلوماتك؟" : "How Do We Keep Your Information Safe?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <p>طبّقنا تدابير أمنية تقنية وتنظيمية مناسبة لحماية أي معلومات شخصية نعالجها. غير أنه لا يمكن ضمان أن أي نقل إلكتروني عبر الإنترنت أو تقنية تخزين آمنة 100%، لذا يجب عليك الوصول إلى الخدمات في بيئة آمنة فحسب.</p>
              ) : (
                <p>We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so you should only access the Services within a secure environment.</p>
              )}
            </div>
          </Card>

          {/* Section 7 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-7">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">7</span>
              {isAr ? "هل نجمع معلومات من القاصرين؟" : "Do We Collect Information From Minors?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <p>لا نجمع بيانات من الأطفال دون سن 18 عاماً ولا نسوّق لهم عن قصد. باستخدامك للخدمات، تُقرّ بأنك بلغت 18 عاماً على الأقل. إذا اكتشفت وجود بيانات قد جمعناها من أطفال دون سن 18 عاماً، يُرجى التواصل معنا على <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline" dir="ltr">digital@sustainabilityexcellence.com</a>.</p>
              ) : (
                <p>We do not knowingly collect data from or market to children under 18 years of age. By using the Services, you represent that you are at least 18 years old. If you discover that data from children under 18 may have been collected, please contact us at <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline">digital@sustainabilityexcellence.com</a>.</p>
              )}
            </div>
          </Card>

          {/* Section 8 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-8">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">8</span>
              {isAr ? "ما هي حقوق الخصوصية الخاصة بك؟" : "What Are Your Privacy Rights?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <p><em>باختصار: يمكنك مراجعة حسابك أو تغييره أو إنهائه في أي وقت.</em></p>
                  <p><strong>سحب موافقتك:</strong> إذا كنا نعتمد على موافقتك لمعالجة معلوماتك الشخصية، يحق لك سحب موافقتك في أي وقت، دون أن يؤثر ذلك على مشروعية المعالجة التي جرت قبل سحب الموافقة.</p>
                  <p><strong>معلومات الحساب.</strong> إذا رغبت في مراجعة المعلومات الواردة في حسابك أو تغييرها أو إنهاء حسابك، يمكنك التواصل معنا باستخدام معلومات الاتصال المقدمة.</p>
                  <p>إذا كانت لديك أسئلة أو تعليقات بشأن حقوق الخصوصية الخاصة بك، يمكنك مراسلتنا على <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline" dir="ltr">digital@sustainabilityexcellence.com</a>.</p>
                </>
              ) : (
                <>
                  <p><em>In Short: You may review, change, or terminate your account at any time.</em></p>
                  <p><strong>Withdrawing your consent:</strong> If we are relying on your consent to process your personal information, you have the right to withdraw your consent at any time. This will not affect the lawfulness of the processing before its withdrawal.</p>
                  <p><strong>Account Information.</strong> If you would at any time like to review or change the information in your account or terminate your account, you may contact us using the contact information provided.</p>
                  <p>If you have questions or comments about your privacy rights, you may email us at <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline">digital@sustainabilityexcellence.com</a>.</p>
                </>
              )}
            </div>
          </Card>

          {/* Section 9 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-9">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">9</span>
              {isAr ? "عناصر التحكم في ميزات عدم التتبع" : "Controls for Do-Not-Track Features"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <p>تتضمن معظم متصفحات الويب وبعض تطبيقات الهواتف المحمولة ميزة "عدم التتبع" (DNT) التي يمكنك تفعيلها. في هذه المرحلة، لم يتم إقرار معيار تقني موحد للتعرف على إشارات DNT وتطبيقها، لذا لا نستجيب حالياً لهذه الإشارات.</p>
              ) : (
                <p>Most web browsers and some mobile operating systems include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online.</p>
              )}
            </div>
          </Card>

          {/* Section 10 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-10">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">10</span>
              {isAr ? "هل نُجري تحديثات على هذا الإشعار؟" : "Do We Make Updates to This Notice?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <p><em>باختصار: نعم، سنحدّث هذا الإشعار عند الضرورة للبقاء ممتثلين للقوانين ذات الصلة.</em></p>
                  <p>قد نحدّث إشعار الخصوصية هذا من وقت لآخر. ستُشار النسخة المحدّثة بتاريخ "مراجعة" محدّث في أعلى الإشعار. نشجعك على مراجعة إشعار الخصوصية هذا بشكل منتظم للاطلاع على كيفية حمايتنا لمعلوماتك.</p>
                </>
              ) : (
                <>
                  <p><em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>
                  <p>We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.</p>
                </>
              )}
            </div>
          </Card>

          {/* Section 11 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-11">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">11</span>
              {isAr ? "كيف يمكنك التواصل معنا بشأن هذا الإشعار؟" : "How Can You Contact Us About This Notice?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <>
                  <p>إذا كانت لديك أسئلة أو تعليقات بشأن هذا الإشعار، يمكنك التواصل مع مسؤول حماية البيانات (DPO) لدينا عبر البريد الإلكتروني على <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline" dir="ltr">digital@sustainabilityexcellence.com</a>، أو مراسلتنا بالبريد العادي على العنوان التالي:</p>
                  <p dir="ltr">Sustainability Excellence Management Consulting Limited<br />Data Protection Officer<br />Incubator Building Masdar City, Workstation 10, Level 1<br />Abu Dhabi, United Arab Emirates</p>
                </>
              ) : (
                <>
                  <p>If you have questions or comments about this notice, you may contact our Data Protection Officer (DPO) by email at <a href="mailto:digital@sustainabilityexcellence.com" className="text-accent hover:underline">digital@sustainabilityexcellence.com</a>, or contact us by post at:</p>
                  <p>Sustainability Excellence Management Consulting Limited<br />Data Protection Officer<br />Incubator Building Masdar City, Workstation 10, Level 1<br />Abu Dhabi, United Arab Emirates</p>
                </>
              )}
            </div>
          </Card>

          {/* Section 12 */}
          <Card className="p-6 md:p-8 border-0 shadow-sm" data-testid="section-privacy-12">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent/10 text-accent text-sm font-bold flex-shrink-0">12</span>
              {isAr ? "كيف يمكنك مراجعة البيانات التي نجمعها أو تحديثها أو حذفها؟" : "How Can You Review, Update, or Delete the Data We Collect From You?"}
            </h2>
            <div className={prose}>
              {isAr ? (
                <p>بناءً على القوانين المعمول بها في بلدك، قد يحق لك طلب الوصول إلى المعلومات الشخصية التي نجمعها منك، وتصحيح أي أخطاء فيها، أو حذفها. وقد تكون هذه الحقوق مقيّدة في بعض الحالات بموجب القانون المعمول به. لطلب مراجعة معلوماتك الشخصية أو تحديثها أو حذفها، يُرجى تعبئة طلب وصول موضوع البيانات وإرساله.</p>
              ) : (
                <p>Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. These rights may be limited in some circumstances by applicable law. To request to review, update, or delete your personal information, please fill out and submit a data subject access request.</p>
              )}
            </div>
          </Card>

        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
